import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Flame, Loader2 } from "lucide-react";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { clearToken } from "@/api";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, name);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left visual panel */}
      <div className="relative hidden lg:block bg-slate-950">
        <img
          src="https://images.pexels.com/photos/3862377/pexels-photo-3862377.jpeg"
          alt="Engineer reviewing blueprints"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-transparent" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center w-10 h-10 bg-[#E50000]">
              <Flame size={22} />
            </span>
            <span className="font-display font-black tracking-tighter text-xl">SPRINKLER TAKEOFF</span>
          </div>
          <div>
            <h1 className="font-display text-5xl font-black tracking-tighter leading-[0.95]">
              Turn sprinkler<br />drawings into<br />
              <span className="text-[#4d7bff]">material takeoffs.</span>
            </h1>
            <p className="mt-6 text-slate-300 max-w-md leading-relaxed">
              Upload a fire sprinkler plan, let AI draft your pipe, fittings, heads and
              hanger counts, review, then export straight to Excel.
            </p>
          </div>
          <div className="text-xs uppercase tracking-[0.25em] text-slate-500">
            Pipe · Fittings · Heads · Hangers
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-white">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <span className="grid place-items-center w-9 h-9 bg-[#E50000] text-white">
              <Flame size={20} />
            </span>
            <span className="font-display font-black tracking-tighter text-lg">SPRINKLER TAKEOFF</span>
          </div>

          <p className="uppercase text-xs font-bold tracking-[0.2em] text-slate-500">
            {mode === "login" ? "Welcome back" : "Get started"}
          </p>
          <h2 className="font-display text-3xl font-black tracking-tight text-slate-950 mt-2">
            {mode === "login" ? "Sign in to your account" : "Create your account"}
          </h2>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="auth-form">
            {mode === "register" && (
              <Field label="Name">
                <input
                  data-testid="name-input"
                  className="auth-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Estimator"
                />
              </Field>
            )}
            <Field label="Email">
              <input
                data-testid="email-input"
                type="email"
                required
                className="auth-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
              />
            </Field>
            <Field label="Password">
              <input
                data-testid="password-input"
                type="password"
                required
                className="auth-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
              />
            </Field>

            {error && (
              <div data-testid="auth-error" className="text-sm text-[#DC2626] bg-red-50 border border-red-200 px-3 py-2">
                {error}
              </div>
            )}

            <button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-[#002FA7] text-white font-semibold px-6 py-3 hover:bg-[#002080] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading && <Loader2 size={18} className="animate-spin" />}
              {mode === "login" ? "Sign in" : "Create account"}
            </button>
          </form>

          <p className="mt-6 text-sm text-slate-600">
            {mode === "login" ? "No account yet? " : "Already have an account? "}
            <button
              data-testid="toggle-auth-mode-btn"
              onClick={() => {
                setMode(mode === "login" ? "register" : "login");
                setError("");
              }}
              className="font-bold text-[#002FA7] hover:underline"
            >
              {mode === "login" ? "Create one" : "Sign in"}
            </button>
          </p>

          <p className="mt-4 text-xs text-slate-400">
            Trouble signing in?{" "}
            <button
              data-testid="reset-session-btn"
              onClick={() => {
                clearToken();
                try {
                  localStorage.clear();
                } catch (e) {}
                window.location.reload();
              }}
              className="underline hover:text-slate-600"
            >
              Reset session
            </button>
          </p>
        </div>
      </div>

      <style>{`
        .auth-input {
          width: 100%;
          border: 1px solid #E2E8F0;
          padding: 0.625rem 0.875rem;
          border-radius: 2px;
          outline: none;
          transition: border-color .15s;
          font-size: 0.95rem;
        }
        .auth-input:focus { border-color: #002FA7; box-shadow: 0 0 0 1px #002FA7; }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="uppercase text-xs font-bold tracking-[0.15em] text-slate-500">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
